import {
  OEM_MAP_CONFIG_FIELDS,
  OEM_MAP_CONFIG_KEYS,
  OEM_MAP_DEFAULTS,
  OEM_REGION_ALIASES,
  cloneConfig,
  diffOEMMapConfig,
  equalOEMConfigValue,
  flattenOEMMapConfig,
  normalizeState,
  validateConfig
} from "./chunk-XKKVLVST.js";

// packages/map/src/index.ts
import {
  loadOEMManifest,
  checkOEMManifestVersion,
  withOEMAbort,
  invalid,
  resolveOEMAsset
} from "@opendfieldmap/core";

// packages/map/src/api.ts
var methods = {
  getState: true,
  update: true,
  getView: true,
  setView: true,
  setZoom: true,
  fitBounds: true,
  setRegion: true,
  setSubregion: true,
  setFloor: true,
  setLocale: true,
  getLocale: true,
  setTheme: true,
  setFeatures: true,
  getResourceState: true,
  retry: true,
  setPointFilter: true,
  getPointFilter: true,
  setMarkerClustering: true,
  setInteractionLocks: true,
  getCustomPoints: true,
  getCustomPoint: true,
  setCustomPoints: true,
  upsertCustomPoints: true,
  removeCustomPoints: true,
  loadCustomPoints: true,
  clearCustomPoints: true,
  getPoint: true,
  loadPoint: true,
  project: true,
  unproject: true,
  on: true,
  resize: true,
  destroy: true
};
function createMapAPI(instance) {
  const api = Object.fromEntries(
    Object.keys(methods).map((key) => [key, instance[key].bind(instance)])
  );
  Object.defineProperties(api, {
    manifest: { enumerable: true, get: () => instance.manifest },
    destroyed: { enumerable: true, get: () => instance.destroyed }
  });
  return Object.freeze(api);
}

// packages/map/src/index.ts
import {
  gameToOEMPosition,
  gameXZToOEMPosition,
  mapToGameXZPosition,
  oemToGamePosition,
  toOEMMapPosition,
  fromOEMMapPosition,
  toOEMLeafletMapPosition
} from "@opendfieldmap/core";
import { OEMError } from "@opendfieldmap/core";
import {
  createOEMCoordinateSnapshot,
  pixelToMapPosition,
  mapToPixelPosition,
  gameXZToMapPosition,
  pixelToGameXZPosition
} from "@opendfieldmap/core";
var runtimePromise;
var loadRuntime = () => runtimePromise ??= import("./runtime-X2LSW2JP.js").catch((error) => {
  runtimePromise = void 0;
  throw error;
});
if (typeof document !== "undefined") void loadRuntime().catch(() => void 0);
async function createOEM(container, options) {
  if (typeof document === "undefined") throw new Error("createOEM must run in a browser");
  if (!options || typeof options !== "object" || Array.isArray(options))
    invalid("options", "Expected an object");
  resolveOEMAsset(options.resources?.baseUrl, options.resources?.manifestPath);
  if (options.customPoints !== void 0 && options.customPointsUrl !== void 0) {
    throw new Error("Pass either customPoints or customPointsUrl, not both");
  }
  options.signal?.throwIfAborted();
  const element = typeof container === "string" ? document.querySelector(container) : container;
  if (!element) throw new Error(`OEM container not found: ${container}`);
  const [manifest, { OEM: OEMClass }] = await withOEMAbort(
    Promise.all([
      options.manifest !== void 0 ? checkOEMManifestVersion(options.manifest) : loadOEMManifest(options.resources, options.signal),
      loadRuntime()
    ]),
    options.signal
  );
  options.signal?.throwIfAborted();
  const instance = new OEMClass(element, manifest, options);
  try {
    void instance.prepareSubregionBoundaries().catch(() => void 0);
    await instance.setFeatures(options.features ?? {});
    if (options.customPointsUrl !== void 0) await instance.loadCustomPoints(options.customPointsUrl);
    options.signal?.throwIfAborted();
    return createMapAPI(instance);
  } catch (error) {
    instance.destroy();
    throw error;
  }
}
export {
  OEMError,
  OEM_MAP_CONFIG_FIELDS,
  OEM_MAP_CONFIG_KEYS,
  OEM_MAP_DEFAULTS,
  OEM_REGION_ALIASES,
  createOEM,
  createOEMCoordinateSnapshot,
  diffOEMMapConfig,
  equalOEMConfigValue,
  flattenOEMMapConfig,
  fromOEMMapPosition,
  gameToOEMPosition,
  gameXZToMapPosition,
  gameXZToOEMPosition,
  mapToGameXZPosition,
  mapToPixelPosition,
  oemToGamePosition,
  pixelToGameXZPosition,
  pixelToMapPosition,
  normalizeState as resolveOEMMapConfig,
  cloneConfig as snapshotOEMMapConfig,
  toOEMLeafletMapPosition,
  toOEMMapPosition,
  validateConfig as validateOEMMapConfig
};
//# sourceMappingURL=index.js.map
